import { useState, useEffect } from "react";
import Layout from "@/components/layout/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const caseStudies = [
// Customer Support category
{
  id: 1,
  title: "24/7 Customer Support for E-commerce Brand",
  description: "How our AI chatbot reduced response times by 85% and increased customer satisfaction for a leading online retailer.",
  category: "customer-support",
  industry: "E-commerce",
  results: ["85% faster response times", "32% increase in customer satisfaction", "68% reduction in support tickets"],
  imageUrl: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80"
},
{
  id: 2,
  title: "Multilingual Support for Travel Agency",
  description: "Implementing AI chatbots that support 12 languages for a global travel company, resulting in expanded market reach.",
  category: "customer-support",
  industry: "Travel",
  results: ["Support in 12 languages", "24/7 availability", "42% cost reduction"],
  imageUrl: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80"
},

// Data Collection category
{
  id: 3,
  title: "Medical Appointment Data Collection",
  description: "Streamlining patient data collection and reducing administrative work for a network of medical clinics.",
  category: "data-collection",
  industry: "Healthcare",
  results: ["75% reduction in paperwork", "92% data accuracy", "4.8/5 patient satisfaction"],
  imageUrl: "https://images.unsplash.com/photo-1581594693702-fbdc51b2763b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80"
},
{
  id: 4,
  title: "Market Research Automation for CPG Brand",
  description: "Automating consumer feedback collection and analysis for a major consumer packaged goods company.",
  category: "data-collection",
  industry: "Consumer Goods",
  results: ["5x more data points collected", "68% cost reduction", "Insights delivered 3x faster"],
  imageUrl: "https://images.unsplash.com/photo-1551135049-8a33b5883817?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80"
},

// Inbound Calls category
{
  id: 5,
  title: "AI Receptionist for Law Firm Network",
  description: "Implementing voice AI to handle client intake calls across 12 locations, improving efficiency and client experience.",
  category: "inbound-calls",
  industry: "Legal",
  results: ["100% call answer rate", "87% accurate call routing", "$158k annual savings"],
  imageUrl: "https://images.unsplash.com/photo-1568992688065-536aad8a12f6?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80"
},
{
  id: 6,
  title: "24/7 Support Line for Insurance Company",
  description: "Creating an AI-powered phone system that handles policy questions and claims initiation around the clock.",
  category: "inbound-calls",
  industry: "Insurance",
  results: ["42% reduction in wait times", "24/7 availability", "98% issue resolution rate"],
  imageUrl: "https://images.unsplash.com/photo-1562654501-a0ccc0fc3fb1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80"
},

// Outbound Calls category
{
  id: 7,
  title: "Appointment Confirmation System for Medical Group",
  description: "Reducing no-shows with automated voice calls that confirm and remind patients about upcoming appointments.",
  category: "outbound-calls",
  industry: "Healthcare",
  results: ["63% reduction in no-shows", "$245k additional annual revenue", "4.7/5 patient satisfaction"],
  imageUrl: "https://images.unsplash.com/photo-1563906267088-b029e7101114?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80"
},
{
  id: 8,
  title: "Restaurant Reservation Confirmation System",
  description: "Implementing AI-powered calls to confirm dining reservations for a restaurant group, reducing no-shows and improving table management.",
  category: "outbound-calls",
  industry: "Hospitality",
  results: ["54% reduction in no-shows", "15% increase in covers", "Improved staff planning"],
  imageUrl: "https://images.unsplash.com/photo-1552566626-52f8b828add9?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80"
},

// Lead Collection category
{
  id: 9,
  title: "Real Estate Lead Qualification System",
  description: "AI-powered solution that pre-qualifies prospects and schedules property viewings automatically for a large realty group.",
  category: "lead-collection",
  industry: "Real Estate",
  results: ["3.2x increase in qualified leads", "27% higher conversion rate", "$310k additional annual revenue"],
  imageUrl: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80"
},
{
  id: 10,
  title: "Event Registration Management",
  description: "Automating attendee registration and follow-up for a conference organizer, enhancing the attendee experience and increasing conversion.",
  category: "lead-collection",
  industry: "Events",
  results: ["78% faster registration process", "42% increase in registrations", "Detailed attendee analytics"],
  imageUrl: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80"
},

// Sales Qualification category
{
  id: 11,
  title: "SaaS Demo Booking Automation",
  description: "Implementing an AI system to qualify leads and schedule product demos for a B2B SaaS company.",
  category: "sales-qualification",
  industry: "Technology",
  results: ["2.3x increase in qualified demos", "42% reduction in sales cycle", "Improved sales team efficiency"],
  imageUrl: "https://images.unsplash.com/photo-1581287053822-fd7bf4f4bfec?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80"
},
{
  id: 12,
  title: "Auto Dealership Lead Qualification",
  description: "Creating an AI-powered system that qualifies vehicle purchase inquiries and schedules test drives for a multi-location dealership.",
  category: "sales-qualification",
  industry: "Automotive",
  results: ["67% increase in test drives", "38% higher conversion rate", "Improved inventory turnover"],
  imageUrl: "https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80"
}];


export default function CaseStudiesPage() {
  const [category, setCategory] = useState("all");

  useEffect(() => {
    // Set page title
    document.title = "AI Automation Case Studies | Success Stories";
  }, []);

  // Filter case studies based on category
  const filteredCaseStudies = category === "all" ?
  caseStudies :
  caseStudies.filter((study) => study.category === category);

  return (
    <Layout data-id="cvfveh66o" data-path="src/pages/CaseStudiesPage.tsx">
      {/* Header section */}
      <div className="bg-gradient-to-b from-gray-50 to-white py-16" data-id="8xxxqg491" data-path="src/pages/CaseStudiesPage.tsx">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="w7jhu2zxp" data-path="src/pages/CaseStudiesPage.tsx">
          <div className="max-w-3xl mx-auto text-center" data-id="lcg9xfkpp" data-path="src/pages/CaseStudiesPage.tsx">
            <h1 className="text-4xl font-bold text-gray-900 mb-6" data-id="imkk6ojm7" data-path="src/pages/CaseStudiesPage.tsx">
              Client Success Stories
            </h1>
            <p className="text-lg text-gray-600 mb-8" data-id="swqj1qkrr" data-path="src/pages/CaseStudiesPage.tsx">
              Discover how businesses across industries have transformed their operations with our AI automation solutions. Browse our case studies to see real-world results.
            </p>
          </div>
        </div>
      </div>
      
      {/* Case studies section */}
      <div className="py-12 bg-white" data-id="8d4c1ncf0" data-path="src/pages/CaseStudiesPage.tsx">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="cbf5hj9kh" data-path="src/pages/CaseStudiesPage.tsx">
          <Tabs defaultValue="all" className="w-full mb-8" data-id="ahu9bvw6s" data-path="src/pages/CaseStudiesPage.tsx">
            <TabsList className="w-full max-w-4xl mx-auto grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 bg-gray-100 p-1 rounded-full h-auto" data-id="tssb30o6u" data-path="src/pages/CaseStudiesPage.tsx">
              <TabsTrigger
                value="all"
                onClick={() => setCategory("all")}
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-lg rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 hover:bg-gray-200 data-[state=active]:hover:from-blue-600 data-[state=active]:hover:to-purple-700" data-id="7svypuepj" data-path="src/pages/CaseStudiesPage.tsx">

                All
              </TabsTrigger>
              <TabsTrigger
                value="customer-support"
                onClick={() => setCategory("customer-support")}
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-lg rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 hover:bg-gray-200 data-[state=active]:hover:from-blue-600 data-[state=active]:hover:to-purple-700" data-id="i841xqti4" data-path="src/pages/CaseStudiesPage.tsx">

                Customer Support
              </TabsTrigger>
              <TabsTrigger
                value="data-collection"
                onClick={() => setCategory("data-collection")}
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-lg rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 hover:bg-gray-200 data-[state=active]:hover:from-blue-600 data-[state=active]:hover:to-purple-700" data-id="x06qh8q6i" data-path="src/pages/CaseStudiesPage.tsx">

                Data Collection
              </TabsTrigger>
              <TabsTrigger
                value="inbound-calls"
                onClick={() => setCategory("inbound-calls")}
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-lg rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 hover:bg-gray-200 data-[state=active]:hover:from-blue-600 data-[state=active]:hover:to-purple-700" data-id="gufth02fc" data-path="src/pages/CaseStudiesPage.tsx">

                Inbound Calls
              </TabsTrigger>
              <TabsTrigger
                value="outbound-calls"
                onClick={() => setCategory("outbound-calls")}
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-lg rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 hover:bg-gray-200 data-[state=active]:hover:from-blue-600 data-[state=active]:hover:to-purple-700" data-id="tkw5n9wop" data-path="src/pages/CaseStudiesPage.tsx">

                Outbound Calls
              </TabsTrigger>
              <TabsTrigger
                value="lead-collection"
                onClick={() => setCategory("lead-collection")}
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-lg rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 hover:bg-gray-200 data-[state=active]:hover:from-blue-600 data-[state=active]:hover:to-purple-700" data-id="3i7r48bpl" data-path="src/pages/CaseStudiesPage.tsx">

                Lead Collection
              </TabsTrigger>
              <TabsTrigger
                value="sales-qualification"
                onClick={() => setCategory("sales-qualification")}
                className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600 data-[state=active]:text-white data-[state=active]:shadow-lg rounded-full px-4 py-2 text-sm font-medium transition-all duration-200 hover:bg-gray-200 data-[state=active]:hover:from-blue-600 data-[state=active]:hover:to-purple-700" data-id="2oxepm13f" data-path="src/pages/CaseStudiesPage.tsx">

                Sales Qualification
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="all" className="mt-8" data-id="4bgxvetyj" data-path="src/pages/CaseStudiesPage.tsx">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="28nz43d8x" data-path="src/pages/CaseStudiesPage.tsx">
                {filteredCaseStudies.map((study) =>
                <CaseStudyCard key={study.id} study={study} data-id="18s29wz71" data-path="src/pages/CaseStudiesPage.tsx" />
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="customer-support" className="mt-8" data-id="ki78d7rrs" data-path="src/pages/CaseStudiesPage.tsx">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="sw6nxmo3f" data-path="src/pages/CaseStudiesPage.tsx">
                {filteredCaseStudies.map((study) =>
                <CaseStudyCard key={study.id} study={study} data-id="ezkqyfiys" data-path="src/pages/CaseStudiesPage.tsx" />
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="data-collection" className="mt-8" data-id="gukrevbin" data-path="src/pages/CaseStudiesPage.tsx">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="n6zbgkx45" data-path="src/pages/CaseStudiesPage.tsx">
                {filteredCaseStudies.map((study) =>
                <CaseStudyCard key={study.id} study={study} data-id="bhcyisk25" data-path="src/pages/CaseStudiesPage.tsx" />
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="inbound-calls" className="mt-8" data-id="j6u5wy4lk" data-path="src/pages/CaseStudiesPage.tsx">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="9wba5ozkl" data-path="src/pages/CaseStudiesPage.tsx">
                {filteredCaseStudies.map((study) =>
                <CaseStudyCard key={study.id} study={study} data-id="8gh692j7l" data-path="src/pages/CaseStudiesPage.tsx" />
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="outbound-calls" className="mt-8" data-id="seip6p742" data-path="src/pages/CaseStudiesPage.tsx">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="btvbfkkry" data-path="src/pages/CaseStudiesPage.tsx">
                {filteredCaseStudies.map((study) =>
                <CaseStudyCard key={study.id} study={study} data-id="sjcad15al" data-path="src/pages/CaseStudiesPage.tsx" />
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="lead-collection" className="mt-8" data-id="fbe56bt7a" data-path="src/pages/CaseStudiesPage.tsx">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="1lcxkufhy" data-path="src/pages/CaseStudiesPage.tsx">
                {filteredCaseStudies.map((study) =>
                <CaseStudyCard key={study.id} study={study} data-id="1a2x1lkk6" data-path="src/pages/CaseStudiesPage.tsx" />
                )}
              </div>
            </TabsContent>
            
            <TabsContent value="sales-qualification" className="mt-8" data-id="uwrx58o2n" data-path="src/pages/CaseStudiesPage.tsx">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="dak517hcc" data-path="src/pages/CaseStudiesPage.tsx">
                {filteredCaseStudies.map((study) =>
                <CaseStudyCard key={study.id} study={study} data-id="g0xbp7150" data-path="src/pages/CaseStudiesPage.tsx" />
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      
      {/* CTA section */}
      <div className="py-16 bg-gray-50" data-id="ruwf5f6mr" data-path="src/pages/CaseStudiesPage.tsx">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="gwevwbad1" data-path="src/pages/CaseStudiesPage.tsx">
          <div className="max-w-3xl mx-auto text-center" data-id="c22a2efu4" data-path="src/pages/CaseStudiesPage.tsx">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="51ajetxpn" data-path="src/pages/CaseStudiesPage.tsx">
              Ready to Become Our Next Success Story?
            </h2>
            <p className="text-lg text-gray-600 mb-8" data-id="sgstxe11o" data-path="src/pages/CaseStudiesPage.tsx">
              Join the hundreds of businesses that have transformed their operations with our AI automation solutions. Book a demo today to discuss your specific needs.
            </p>
            <Button asChild size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700" data-id="0txj4wjid" data-path="src/pages/CaseStudiesPage.tsx">
              <Link to="/contact" data-id="s64brvmtz" data-path="src/pages/CaseStudiesPage.tsx">Book a Demo</Link>
            </Button>
          </div>
        </div>
      </div>
    </Layout>);

}

// Case Study Card Component
interface CaseStudyProps {
  study: {
    id: number;
    title: string;
    description: string;
    category: string;
    industry: string;
    results: string[];
    imageUrl: string;
  };
}

function CaseStudyCard({ study }: CaseStudyProps) {
  return (
    <Card className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-shadow" data-id="1s3cu5pmt" data-path="src/pages/CaseStudiesPage.tsx">
      <div className="h-48 overflow-hidden" data-id="g2yy8o4xv" data-path="src/pages/CaseStudiesPage.tsx">
        <img
          src={study.imageUrl}
          alt={study.title}
          className="w-full h-full object-cover transition-transform hover:scale-105 duration-500" data-id="wiekxn01s" data-path="src/pages/CaseStudiesPage.tsx" />

      </div>
      <CardContent className="p-6" data-id="0csg7q538" data-path="src/pages/CaseStudiesPage.tsx">
        <div className="flex items-center mb-4" data-id="oana5ipym" data-path="src/pages/CaseStudiesPage.tsx">
          <span className="text-sm font-medium text-purple-600 bg-purple-50 px-3 py-1 rounded-full" data-id="2dz6touuv" data-path="src/pages/CaseStudiesPage.tsx">
            {study.industry}
          </span>
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="594yzkxo1" data-path="src/pages/CaseStudiesPage.tsx">{study.title}</h3>
        <p className="text-gray-600 mb-4" data-id="acljxo04g" data-path="src/pages/CaseStudiesPage.tsx">{study.description}</p>
        
        <div className="mb-4" data-id="6ll8xzjd9" data-path="src/pages/CaseStudiesPage.tsx">
          <h4 className="text-sm font-medium text-gray-900 mb-2" data-id="ohv60720e" data-path="src/pages/CaseStudiesPage.tsx">Results:</h4>
          <ul className="space-y-1" data-id="ep1swq5a2" data-path="src/pages/CaseStudiesPage.tsx">
            {study.results.map((result, index) =>
            <li key={index} className="flex items-start" data-id="8wbumnwb0" data-path="src/pages/CaseStudiesPage.tsx">
                <div className="text-green-500 mr-2" data-id="k8sb98s49" data-path="src/pages/CaseStudiesPage.tsx">✓</div>
                <span className="text-gray-600 text-sm" data-id="xbs00rphs" data-path="src/pages/CaseStudiesPage.tsx">{result}</span>
              </li>
            )}
          </ul>
        </div>
        
        <Button variant="ghost" className="text-gray-400 p-0 cursor-not-allowed group" disabled data-id="ej2le4x32" data-path="src/pages/CaseStudiesPage.tsx">
          <span className="flex items-center" data-id="cluzdv1z5" data-path="src/pages/CaseStudiesPage.tsx">
            Read full case study 
            <ArrowRight className="ml-1 h-4 w-4" data-id="17qgrrpri" data-path="src/pages/CaseStudiesPage.tsx" />
          </span>
        </Button>
      </CardContent>
    </Card>);

}